if (!customElements.get('variant-selects')) {
  customElements.define(
    'variant-selects',
    class VariantSelects extends HTMLElement {
      constructor() {
        super();
      }

      get selectedOptionValues() {
        return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
          ({ dataset }) => dataset.optionValueId
        );
      }

      getProductVariants() {
        const variantsScript = this.querySelector('[data-product-variants]');
        if (!variantsScript) return [];
        try {
          return JSON.parse(variantsScript.textContent);
        } catch (e) {
          console.error('Failed to parse product variants:', e);
          return [];
        }
      }

      findCheapestAvailableVariant(matchOption1 = null) {
        const variants = this.getProductVariants();
        let cheapestVariant = null;
        let cheapestPrice = Infinity;

        for (const variant of variants) {
          // Skip unavailable variants
          if (!variant.available) continue;

          // If matchOption1 is specified, only consider variants with that option1 value
          if (matchOption1 && variant.option1 !== matchOption1) continue;

          // Check if this is the cheapest so far
          if (variant.price < cheapestPrice) {
            cheapestPrice = variant.price;
            cheapestVariant = variant;
          }
        }

        return cheapestVariant;
      }

      findCheapestVariantMatchingOption(optionPosition, optionValue) {
        const variants = this.getProductVariants();
        const key = `option${optionPosition}`;
        let cheapestVariant = null;
        let cheapestPrice = Infinity;

        for (const variant of variants) {
          if (!variant.available) continue;
          if (variant[key] !== optionValue) continue;
          if (variant.price < cheapestPrice) {
            cheapestPrice = variant.price;
            cheapestVariant = variant;
          }
        }

        return cheapestVariant;
      }

      findVariantByCurrentSelection() {
        const variants = this.getProductVariants();

        // Get all option inputs to match values to titles
        const optionInputs = this.querySelectorAll('fieldset input:checked, select option[selected]');
        const selectedOptions = [];

        optionInputs.forEach(input => {
          if (input.tagName === 'OPTION') {
            selectedOptions.push(input.value);
          } else if (input.tagName === 'INPUT') {
            selectedOptions.push(input.value);
          }
        });

        // Find variant that matches all selected options
        for (const variant of variants) {
          const variantOptions = [variant.option1, variant.option2, variant.option3].filter(Boolean);

          // Check if all selected options match this variant's options
          let matches = true;
          for (let i = 0; i < selectedOptions.length; i++) {
            if (selectedOptions[i] !== variantOptions[i]) {
              matches = false;
              break;
            }
          }

          if (matches) {
            return variant;
          }
        }

        return null;
      }

      getInputForEventTarget(target) {
        return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
      }

      connectedCallback() {
        this.updateOptionAvailability();

        this.addEventListener('change', (event) => {
          // Skip if we're in the middle of auto-selecting
          if (this._isAutoSelecting) {
            return;
          }

          const target = this.getInputForEventTarget(event.target);
          this.updateSelectedSwatchValue(event);

          // A variant that matches the selection (even if out of stock) is kept as-is,
          // so the user can use the notify-me / sold-out flow on that specific variant.
          // Only auto-switch when no variant exists for the combo at all.
          const matchingVariant = this.findVariantByCurrentSelection();
          let selectedVariant = matchingVariant || null;
          let autoSelected = false;

          if (!selectedVariant) {
            // The clicked combo doesn't map to any variant on this product.
            // Preserve the clicked option value and find the cheapest available variant matching it;
            // other options will be adjusted to valid values.
            const changedContainer = target.closest('.product-form__input');
            const allContainers = Array.from(this.querySelectorAll('.product-form__input'));
            const changedIndex = allContainers.indexOf(changedContainer);
            const optionPosition = changedIndex + 1;
            const clickedValue = target.tagName === 'INPUT' || target.tagName === 'OPTION'
              ? target.value
              : target.textContent.trim();

            let cheapestVariant = this.findCheapestVariantMatchingOption(optionPosition, clickedValue);
            if (!cheapestVariant) {
              cheapestVariant = this.findCheapestAvailableVariant();
            }

            if (cheapestVariant) {
              this.selectVariantOptions(cheapestVariant);
              selectedVariant = cheapestVariant;
              autoSelected = true;
            }
          }

          this.updateOptionAvailability();

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, {
            data: {
              event,
              target,
              selectedOptionValues: this.selectedOptionValues,
              autoSelected,
              selectedVariant,
            },
          });
        });
      }

      getCurrentOptionValues() {
        const containers = Array.from(this.querySelectorAll('.product-form__input'));
        return containers.map((container) => {
          const checked = container.querySelector('input[type="radio"]:checked');
          if (checked) return checked.value;
          const select = container.querySelector('select');
          if (select) return select.value;
          return null;
        });
      }

      updateOptionAvailability() {
        const variants = this.getProductVariants();
        const containers = Array.from(this.querySelectorAll('.product-form__input'));
        const currentValues = this.getCurrentOptionValues();

        const hasAvailableMatch = (position, value) => {
          const key = `option${position}`;
          for (const variant of variants) {
            if (!variant.available) continue;
            if (variant[key] !== value) continue;
            let matches = true;
            for (let i = 0; i < currentValues.length; i++) {
              if (i === position - 1) continue;
              if (currentValues[i] == null) continue;
              if (variant[`option${i + 1}`] !== currentValues[i]) {
                matches = false;
                break;
              }
            }
            if (matches) return true;
          }
          return false;
        };

        containers.forEach((container, containerIndex) => {
          const optionPosition = containerIndex + 1;

          container.querySelectorAll('input[type="radio"]').forEach((input) => {
            const available = hasAvailableMatch(optionPosition, input.value);
            input.classList.toggle('disabled', !available);
          });

          const selectEl = container.querySelector('select');
          if (selectEl) {
            Array.from(selectEl.options).forEach((option) => {
              const available = hasAvailableMatch(optionPosition, option.value);
              option.classList.toggle('disabled', !available);
            });
          }
        });
      }

      selectVariantOptions(variant) {
        // Helper method to programmatically select variant options
        // Temporarily disable change events to prevent loops
        this._isAutoSelecting = true;

        const options = [variant.option1, variant.option2, variant.option3].filter(Boolean);

        options.forEach((optionValue, index) => {
          const fieldsets = this.querySelectorAll('fieldset');
          const selects = this.querySelectorAll('select');

          // Handle radio/button inputs
          if (fieldsets[index]) {
            const input = fieldsets[index].querySelector(`input[value="${CSS.escape(optionValue)}"]`);
            if (input && !input.checked) {
              input.checked = true;
              // Trigger change on the input directly without clicking the label
              input.dispatchEvent(new Event('change', { bubbles: false }));
            }
          }

          // Handle select dropdowns
          if (selects[index]) {
            const option = Array.from(selects[index].options).find(opt => opt.value === optionValue);
            if (option) {
              selects[index].value = optionValue;
              // Update selected attribute
              Array.from(selects[index].options).forEach(opt => opt.removeAttribute('selected'));
              option.setAttribute('selected', 'selected');
            }
          }
        });

        // Update the displayed selected values
        this.querySelectorAll('[data-selected-swatch-value]').forEach((elem, index) => {
          if (options[index]) {
            elem.textContent = options[index];
          }
        });

        // Re-enable change events
        setTimeout(() => {
          this._isAutoSelecting = false;
        }, 100);
      }

      updateSelectedSwatchValue({ target }) {
        const { value, tagName } = target;

        if (tagName === 'SELECT' && target.selectedOptions.length) {
          Array.from(target.options)
            .find((option) => option.getAttribute('selected'))
            .removeAttribute('selected');
          target.selectedOptions[0].setAttribute('selected', 'selected');

          const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
          const selectedDropdownSwatchValue = target
            .closest('.product-form__input')
            .querySelector('[data-selected-value] > .swatch');
          if (!selectedDropdownSwatchValue) return;
          if (swatchValue) {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
            selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
          } else {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
            selectedDropdownSwatchValue.classList.add('swatch--unavailable');
          }

          selectedDropdownSwatchValue.style.setProperty(
            '--swatch-focal-point',
            target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
          );
        } else if (tagName === 'INPUT' && target.type === 'radio') {
          const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
          if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
        }
      }
    }
  );
}
